import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { User } from '@mitel/cloudlink-sdk';
import { UserLicense } from '@mitel/cloudlink-sdk/billing';
import { HighLevelProductDetail, ProductLicense, ProductLicenseItemServiceInf } from '../interfaces/product-licenses-interfaces';
import { AppInjector } from './../../app-injector';
import { AdminService } from './admin.service';
import { AccountsService } from './accounts.service';
import { OrdersService } from './orders.service';
import { productCodes, zappaPhoneCodes } from './../constants/constants';
import { IntegCustomerAdminPortalService } from './integ-customer-admin-portal.service';

const logContext = 'UserProdZappaPhoneService';

@Injectable({
  providedIn: 'root'
})
export class UserProdZappaPhoneService implements ProductLicenseItemServiceInf {
  private adminService: AdminService;
  private accountService: AccountsService;
  private ordersService: OrdersService;
  private translateService: TranslateService;
  private capIntegService: IntegCustomerAdminPortalService;

  constructor() { }

  initializeServices() {
    if (!this.adminService)
      this.adminService = AppInjector.injector.get(AdminService);

    if (!this.accountService)
      this.accountService = AppInjector.injector.get(AccountsService);

    if (!this.ordersService)
      this.ordersService = AppInjector.injector.get(OrdersService);

    if (!this.translateService)
      this.translateService = AppInjector.injector.get(TranslateService);

    if (!this.capIntegService)
      this.capIntegService = AppInjector.injector.get(IntegCustomerAdminPortalService);
  }

  async canBeAdded(user: User, product: ProductLicense, isSubComponent?: boolean): Promise<boolean> {
    return Promise.resolve(false);
  }

  async canBeSelected(user: User): Promise<boolean> {
    this.initializeServices();
    // CLSD-5282 - No longer want to see the Zoom Product as a standalone product in the UI if the user has MiVB
    // Instead it should be shown as a sub-product/service of the MiVB product - return false
    // Otherwise the Zoom product will be shown as a standalone product in the UI - return true
    return this.adminService.getUserWithoutFrills(user.userId, 'tags').then( async freshUserWithTags => {
      let tags = freshUserWithTags.tags || {};
      if (tags?.products?.includes('mivb')) {
        // If CAP is NOT enabled: Should not have Zappa Phone product, i.e. should not be able to reach this function
        // I expect this to always return false, but testing for completeness
        return this.capIntegService.isCAPFeatureEnabled({accountId: user.accountId} as any).then((isCAPFeatureEnabled) => !isCAPFeatureEnabled);
      } else {
        return true;
      }
    });
  }

  async hasRequiredAccountIntegrations(user: User, accountIntegrations: string[]): Promise<boolean> {
    this.initializeServices();
   
    const isIntegrationAdded = accountIntegrations?.includes(productCodes.ZAPPA);

    if (isIntegrationAdded) {
      // UI is not allowed to assign licenses, but the license needs to be shown if assigned to the user 
      const userLicense = await this.isLicenseEnabledForUser(user);
      return !!userLicense;
    }
    return false;
  }

  getConfigurationDialog(setupComplete?: boolean, user?: User): Promise<any> {
    return Promise.resolve(undefined);
  }
  
  async isSetupComplete(user: User): Promise<boolean> {
    this.initializeServices();

    const userLicense = await this.isLicenseEnabledForUser(user);
    return !!userLicense;
  }
  
  async handleProductStateChange(user: User, state: boolean, isSubComponent?: boolean): Promise<any> {
    console.warn(`${logContext}.handleProductStateChange - Trying to assign Zappa license in the UI`);
    return Promise.resolve(undefined);
  }

  isButtonDisabled(user: User, product: ProductLicense, isSubComponent?: boolean): Promise<boolean> {
    return Promise.resolve(true);
  }
  
  async isProductEnabled(user: User, isSubComponent?: boolean): Promise<boolean> {
    this.initializeServices();

    return await this.isSetupComplete(user);
  }

  async getDisplayName?(user: User, isSubComponent?: boolean): Promise<string> {
    const userLicense = await this.isLicenseEnabledForUser(user);
    return userLicense ?
      'product_licenses.zappaPhone.name' :
      '';
  }

  async getSubComponentConfigurationDetails(user: User, product?: ProductLicense): Promise<any> {
    const details: any = {
      subComponentNames: [],
      subComponentLabel: 'product_licenses.zappaPhone.license_details.title',
      subComponent: [],
      subComponentEditable: false
    };

    const highLevelProductDetails = await this.getHighLevelProductDetails(user);
    if (highLevelProductDetails.length > 0) {
      details.highLevelProductDetails = highLevelProductDetails;
    }

    return Promise.resolve(details);
  }

  async getHighLevelProductDetails(user: User): Promise<HighLevelProductDetail[]> {
    this.initializeServices();

    const details = [];
    const userLicense = await this.isLicenseEnabledForUser(user);

    if (userLicense && userLicense['additionalConfiguration']) {
      const additionalConfiguration = userLicense['additionalConfiguration'];
      const prefix = 'product_licenses.zappaPhone.license_details';
      const extension = this.translateService.instant(`${prefix}.extension`);
      if (additionalConfiguration.desktopNumber) {
        const primaryDesktop = extension + `: ${additionalConfiguration.desktopNumber} (` + this.translateService.instant(`${prefix}.desktop`) + ')';
        details.push({ primary: primaryDesktop, secondaries: [] });
      }
      if (additionalConfiguration.number) {
        const primaryMobile = extension + `: ${additionalConfiguration.number} (` + this.translateService.instant(`${prefix}.mobile`) + ')';
        details.push({ primary: primaryMobile, secondaries: [] });
      }
    }
    if (userLicense && details.length === 0) {
      // User has license but no details were found
      details.push({
        primary: this.translateService.instant('product_licenses.no_details_found'),
        secondaries: []
      });
    }
    return details;
  }
  
  async isLicenseEnabledForUser(user: User): Promise<UserLicense | undefined> {
    this.initializeServices();

    try {
      if (user.userId) { // no need to ask for licenses if no userId, likely a new user is being created
        const userLicenses = await this.ordersService.getUserLicenses(user.userId);

        return userLicenses.find(license => {
          return license.enable && zappaPhoneCodes.USER_PRODUCT === license.name;
        });
      }
    } catch (error) {
      console.error(`${logContext}.isLicenseEnabledForUser - Failed to get user licenses for Zappa Phone: `, error);
    }
  }
}
